import * as React from 'react';
import { cn } from '../lib/utils';
import { fieldClassName, useFieldControl } from '../lib/field';

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  children?: React.ReactNode;
  /**
   * Sized like a small button (32px, 12px medium text), for a toolbar beside
   * `Button size="sm"`; on touch devices it keeps a 44px target.
   */
  compact?: boolean;
};

// A chevron in the muted foreground color (#526071), drawn as the select's
// background so the control stays a single element callers can size. No
// spaces or quotes in the class: a space splits it into several classes, and
// an escaped quote is not what Tailwind finds in the source.
const chevron =
  'bg-[url(data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%2716%27%20height=%2716%27%20viewBox=%270%200%2024%2024%27%20fill=%27none%27%20stroke=%27%23526071%27%20stroke-width=%272%27%20stroke-linecap=%27round%27%20stroke-linejoin=%27round%27%3E%3Cpath%20d=%27m6%209%206%206%206-6%27/%3E%3C/svg%3E)] bg-[length:1rem] bg-[position:right_0.75rem_center] bg-no-repeat';

/**
 * The platform's own select, styled as a Clear field: native keyboard, touch
 * and screen-reader behavior on every device.
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, compact = false, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        fieldClassName,
        chevron,
        'flex cursor-pointer appearance-none [&>option]:bg-popover [&>option]:text-popover-foreground',
        compact
          ? 'h-8 bg-[length:0.875rem] bg-[position:right_0.625rem_center] py-1 pl-3 pr-8 text-xs font-medium pointer-coarse:h-11'
          : 'h-9 py-1.5 pl-3 pr-9',
        className
      )}
      {...useFieldControl(props)}
    >
      {children}
    </select>
  )
);

Select.displayName = 'Select';

export interface SelectFilterProps extends Omit<SelectProps, 'compact' | 'aria-label'> {
  /** What the choice is for, shown beside it: "Order", "Show". */
  label: string;
}

/**
 * A choice that reshapes a list (its order, a filter), in a toolbar beside
 * small buttons: a visible label, then the compact select. The label names the
 * control for everyone, so it never stands alone as an unlabeled box.
 */
export const SelectFilter = React.forwardRef<HTMLSelectElement, SelectFilterProps>(
  ({ label, className, children, ...props }, ref) => (
    <label className="inline-flex min-w-0 items-center gap-2">
      <span className="shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      <Select ref={ref} compact className={cn('min-w-0 flex-1', className)} {...props}>
        {children}
      </Select>
    </label>
  )
);

SelectFilter.displayName = 'SelectFilter';
